import type { Appointment } from '@medplum/fhirtypes';
import type { ReactNode } from 'react';

import { Caption, Stack, Text } from '@eluve/components';

import { FhirStatusTag } from '../../components/FhirStatusTag';
import { EM_DASH } from '../../core/copy';
import type { FhirView } from '../../core/view-contract';

import {
  appointmentDayText,
  appointmentTimeRangeText,
  participantDisplay,
} from './format';

/**
 * The exported Appointment component. The side-panel variant is the primary
 * one (calendar detail panes); the read card covers standalone placement.
 */
export const AppointmentView: FhirView<Appointment> = (props) => {
  if (props.ctx.placement === 'side-panel') {
    return <AppointmentSidePanelView {...props} />;
  }
  return <AppointmentReadView {...props} />;
};

function appointmentTitle(data: Appointment): string {
  return (
    participantDisplay(data, 'Patient') ?? data.description ?? 'Appointment'
  );
}

function whenText(data: Appointment): string {
  const day = appointmentDayText(data);
  const range = appointmentTimeRangeText(data);
  if (!day) return EM_DASH;
  return range ? `${day} · ${range}` : day;
}

function PanelSection({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <Stack gap={1} align="stretch">
      <Caption color="tertiary">{label}</Caption>
      {children}
    </Stack>
  );
}

/** Calendar detail pane: who, when, status, provider, location. */
const AppointmentSidePanelView: FhirView<Appointment> = ({ data }) => {
  const provider = participantDisplay(data, 'Practitioner');
  const location = participantDisplay(data, 'Location');
  return (
    <Stack
      gap={5}
      align="stretch"
      data-view="appointment-side-panel"
      className="p-1"
    >
      <PanelSection label="Appointment">
        <Text size="md" weight="semibold">
          {appointmentTitle(data)}
        </Text>
      </PanelSection>
      <PanelSection label="When">
        <Text size="sm" weight="regular">
          {whenText(data)}
        </Text>
      </PanelSection>
      <PanelSection label="Status">
        <div>
          <FhirStatusTag status={data.status} />
        </div>
      </PanelSection>
      <PanelSection label="Provider">
        <Text size="sm" weight="regular">
          {provider ?? EM_DASH}
        </Text>
      </PanelSection>
      <PanelSection label="Location">
        <Text size="sm" weight="regular">
          {location ?? EM_DASH}
        </Text>
      </PanelSection>
      {data.comment && (
        <PanelSection label="Notes">
          <Text size="sm" weight="regular">
            {data.comment}
          </Text>
        </PanelSection>
      )}
    </Stack>
  );
};

/** Standalone card for main-placement renders. */
const AppointmentReadView: FhirView<Appointment> = ({ data }) => (
  <div
    data-view="appointment-read"
    className="rounded-lg border border-border-primary bg-bg-primary p-4"
  >
    <Stack direction="horizontal" gap={2} align="center" justify="between">
      <Text inline size="sm" weight="semibold">
        {appointmentTitle(data)}
      </Text>
      <FhirStatusTag status={data.status} />
    </Stack>
    <Text size="xs" color="tertiary" className="mt-1">
      {whenText(data)}
    </Text>
  </div>
);
